import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useHistory, useLocation } from "react-router";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui";
import { FormRouteServices, FormServices, RouteCode } from "@/services/bedrock";
import { WebComponents } from "@/interfaces";
import { useAccountSettings, useSocialCoachSession } from "@/utils";
import { FormDefaultsDialog } from "../FormDefaultsDialog";
import {
  RouteBlocker,
  ReviewSites,
  formUsesReviewSites,
  buildRouteUrl,
  explainerSentences,
  formTypeNoun,
  matchingExample,
  routeBlocker,
  routeParams,
  routeRef,
  useFormRouteBuilder,
} from "./useFormRouteBuilder";
import { DestinationPicker } from "./components/DestinationPicker";
import { RouteCodePicker, sortRouteCodes } from "./components/RouteCodePicker";
import { FormCardWhichForm } from "./components/FormCardWhichForm";
import { FormCardWhoAsks } from "./components/FormCardWhoAsks";
import { FormCardClient } from "./components/FormCardClient";
import { FormCardCampaign } from "./components/FormCardCampaign";
import { RouteOutput, routeStatusText } from "./components/RouteOutput";
import { MERGE_FIELD_COPY, mergeFieldInputId, MergeFieldTable, visibleFieldKeys } from "./components/MergeFieldTable";
import { StickyRouteBar, useScrolledPast } from "./components/StickyRouteBar";
import { FormRoutesIntro } from "./components/FormRoutesIntro";
import { RouteExamples } from "./components/RouteExamples";
import { CrmConnectionNote } from "./components/CrmConnectionNote";
import { SectionLabel, STEP_IDS } from "./components/RouteCard";
import { RouteExplainer } from "./components/RouteExplainer";
import { RouteTester } from "./components/RouteTester";
import { RouteHowTo } from "./components/RouteHowTo";
import { RouteReference } from "./components/RouteReference";

export const FORM_ROUTE_CODES_QUERY_KEY = ["formRouteCodes"];
export const FORM_DEFAULTS_QUERY_KEY = ["formDefaults"];

/**
 * Form Routes, a page inside the Forms tab (`/reputation/forms/routes`) so it can be linked to,
 * bookmarked and left with the browser's Back button. The Reputation header carries its title and
 * the way back to Forms.
 */
export function FormRoutesPage() {
  const queryClient = useQueryClient();
  const { coachId } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();
  const canWrite = hasWritePermission(WebComponents.REPUTATION_MANAGEMENT);
  const location = useLocation();
  const history = useHistory();
  const [pinnedFormGuid] = useState(() => new URLSearchParams(location.search).get("form"));
  const builder = useFormRouteBuilder(coachId, pinnedFormGuid);
  // The pin applies once; drop it so a refresh keeps the admin's later changes
  useEffect(() => {
    if (pinnedFormGuid) history.replace(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { state, update } = builder;
  const [defaultsDialogOpen, setDefaultsDialogOpen] = useState(false);

  const {
    data: codesData,
    isError: codesFailed,
    isSuccess: codesLoaded,
    refetch: refetchCodes,
  } = useQuery({
    queryKey: FORM_ROUTE_CODES_QUERY_KEY,
    queryFn: () => FormRouteServices.getCodes(),
  });
  const codes = useMemo(() => sortRouteCodes(codesData?.content ?? []), [codesData]);
  const selectedCode = codes.find(code => code.id === state.codeId) ?? codes[0] ?? null;

  // A restored code that's no longer listed (disabled since) falls back to the first one
  useEffect(() => {
    if (selectedCode && selectedCode.id !== state.codeId) update({ codeId: selectedCode.id });
  }, [selectedCode?.id, state.codeId, update]);

  // Until TotalExpert's connection ships every account reads as not connected; the note says so
  const { data: crmStatus } = useQuery({
    queryKey: ["formRouteCrmStatus"],
    queryFn: () => FormRouteServices.getCrmStatus(),
  });
  const teStatus = crmStatus?.content.find(status => status.source === "TE");

  const { data: formsData, isLoading: formsLoading } = useQuery({
    queryKey: ["forms", { page: 1, perPage: 100 }],
    queryFn: () => FormServices.getForms({ page: 1, perPage: 100, active: true }),
  });
  const forms = formsData?.content ?? [];

  const {
    data: defaults,
    isLoading: defaultsLoading,
    refetch: refetchDefaults,
  } = useQuery({
    queryKey: FORM_DEFAULTS_QUERY_KEY,
    queryFn: () => FormServices.getCoachDefaults(),
  });
  const accountDefault =
    defaults?.find(d => d.formType === state.formType && (d.segmentationDetailId ?? null) === null) ?? null;

  const saveDefault = useMutation({
    mutationFn: (formId: number) =>
      FormServices.updateCoachDefaults({
        defaults: [
          ...(defaults ?? []).map(d => ({
            formType: d.formType,
            formId: d.formId,
            segmentationDetailId: d.segmentationDetailId ?? undefined,
          })),
          { formType: state.formType, formId, segmentationDetailId: undefined },
        ],
      }),
    onSuccess: () => {
      toast.success(`Saved as the ${formTypeNoun(state.formType)} default`);
      queryClient.invalidateQueries({ queryKey: FORM_DEFAULTS_QUERY_KEY });
    },
    onError: (e: unknown) => toast.error(typeof e === "string" ? e : "The default couldn't be saved. Try again."),
  });

  const ref = routeRef(state);
  const specificForm = forms.find(form => form.guid === state.formGuid) ?? null;
  const blocker = routeBlocker(state, {
    hasAccountDefault: !!accountDefault,
    hasCode: !!selectedCode,
    hasSpecificForm: !!specificForm,
    codeError: codesFailed || (codesLoaded && !codes.length),
  });
  const url = selectedCode && ref ? buildRouteUrl(selectedCode.url, ref, routeParams(state)) : null;
  const previewForm = state.formMode === "type" ? accountDefault?.form : specificForm;
  const formLabel = formTypeNoun(state.formType);
  const reviewSites: ReviewSites =
    state.formMode === "type" || !specificForm ? "maybe" : formUsesReviewSites(specificForm) ? "yes" : "no";

  const routeCardRef = useRef<HTMLElement | null>(null);
  const [routeCard, setRouteCard] = useState<HTMLElement | null>(null);
  const scrolledPast = useScrolledPast(routeCard);
  const statusText = routeStatusText(blocker, formLabel, state.destination);

  // Another CRM starts blank, so its mapping is the first thing to fill in
  const [mergeFieldsOpen, setMergeFieldsOpen] = useState(state.destination === "crm");
  useEffect(() => {
    if (state.destination === "crm") setMergeFieldsOpen(true);
  }, [state.destination]);

  /** The status line's link: scroll to what's missing and put the cursor in it. */
  const handleFix = (missing: RouteBlocker) => {
    const focus = (selector: string) =>
      window.setTimeout(() => document.querySelector<HTMLElement>(selector)?.focus({ preventScroll: true }), 0);
    const reveal = (id: string) =>
      document.getElementById(id)?.scrollIntoView?.({ behavior: "smooth", block: "start" });

    if (missing === "missing_merge_fields") {
      setMergeFieldsOpen(true);
      routeCardRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      focus(`#${mergeFieldInputId("player")}`);
    } else if (missing === "missing_default" || missing === "missing_form") {
      reveal(STEP_IDS.form);
      focus(`#${STEP_IDS.form} [data-testid="missing-default"] [role="combobox"], #${STEP_IDS.form} input`);
    } else if (missing === "code_error") {
      refetchCodes();
    } else if (missing === "missing_player") {
      reveal(STEP_IDS.player);
      focus(`#${STEP_IDS.player} input`);
    }
  };

  const example = matchingExample(state);

  /** The URL flash confirms the pick; on short windows the route card may be off-screen, so bring it in. */
  const handlePickExample = (key: Parameters<typeof builder.pickExample>[0]) => {
    builder.pickExample(key);
    const card = routeCardRef.current?.getBoundingClientRect();
    if (card && (card.top < 0 || card.bottom > window.innerHeight)) {
      routeCardRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }
  };

  const handleCodeCreated = (code: RouteCode) => {
    queryClient.setQueryData<{ content: RouteCode[] }>(FORM_ROUTE_CODES_QUERY_KEY, prev => ({
      content: [...(prev?.content ?? []), code],
    }));
    update({ codeId: code.id });
  };

  if (formsLoading || defaultsLoading) {
    return (
      <div className="max-w-[960px] mx-auto p-6 flex flex-col gap-4">
        <Skeleton className="h-40 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  return (
    // The sticky bar sits outside the column, and outside the tab's padding, so it spans the page
    <div className="-mx-4 -mt-4">
      <StickyRouteBar visible={scrolledPast} url={url} blocker={blocker} statusText={statusText} onFix={handleFix} />
      <div className="max-w-[960px] mx-auto px-6 pt-4 pb-10 flex flex-col gap-5">
        <div className="pt-2">
          <FormRoutesIntro />
        </div>

        <RouteExamples selected={example} onPick={handlePickExample} />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <DestinationPicker destination={state.destination} onChange={builder.setDestination} />
          <RouteCodePicker
            codes={codes}
            selectedId={selectedCode?.id ?? null}
            onSelect={codeId => update({ codeId })}
            onCreated={handleCodeCreated}
            canCreate={canWrite}
          />
        </div>

        {state.destination === "totalexpert" && teStatus && !teStatus.connected && <CrmConnectionNote />}

        <RouteOutput
          ref={element => {
            routeCardRef.current = element;
            setRouteCard(element);
          }}
          url={url}
          blocker={blocker}
          statusText={statusText}
          previewUrl={previewForm?.testRequest?.url ?? null}
          restored={builder.restored}
          onReset={builder.reset}
          onFix={handleFix}
          mergeFieldsTitle={`${MERGE_FIELD_COPY[state.destination].title} (${visibleFieldKeys(state).length})`}
          mergeFieldsOpen={mergeFieldsOpen}
          onMergeFieldsOpenChange={setMergeFieldsOpen}
        >
          <MergeFieldTable state={state} setValue={builder.setValue} />
        </RouteOutput>

        <SectionLabel>Adjust the route</SectionLabel>
        <FormCardWhichForm
          state={state}
          forms={forms}
          hasAccountDefault={!!accountDefault}
          canSaveDefault={canWrite}
          savingDefault={saveDefault.isPending}
          onChange={update}
          onSaveDefault={formId => saveDefault.mutate(formId)}
          onManageDefaults={() => setDefaultsDialogOpen(true)}
        />
        <FormCardWhoAsks state={state} onChange={update} />
        <FormCardClient state={state} onChange={update} setEnabled={builder.setEnabled} />
        <FormCardCampaign state={state} setUtm={builder.setUtm} />

        <SectionLabel>Check it</SectionLabel>
        <RouteExplainer
          sentences={explainerSentences(state, { specificFormName: specificForm?.displayName, reviewSites })}
        />
        <RouteTester
          state={state}
          routeRef={ref}
          routeUrl={selectedCode?.url ?? null}
          formTypeLabel={formLabel}
          accountDefaultFormId={accountDefault?.formId ?? null}
          disabled={blocker === "missing_form" || blocker === "missing_player"}
          reviewSites={reviewSites}
        />

        <SectionLabel>Ship it</SectionLabel>
        <RouteHowTo destination={state.destination} url={url} example={example} />
        <RouteReference />

        <FormDefaultsDialog
          open={defaultsDialogOpen}
          onOpenChange={next => {
            setDefaultsDialogOpen(next);
            if (!next) refetchDefaults();
          }}
        />
      </div>
    </div>
  );
}
